import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';
import './RektorExamResults.css';

/* ── animated counter ── */
function useCounter(target, duration = 900) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!target) { setVal(0); return; }
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(target * ease));
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
}

function AnimStat({ value, label }) {
  const displayed = useCounter(value);
  return (
    <div className="re-stat">
      <span className="re-stat-val">{displayed.toLocaleString('tr')}</span>
      <span className="re-stat-label">{label}</span>
    </div>
  );
}

/* ── helpers ── */
function pctColor(v) {
  if (v == null) return '#475569';
  if (v >= 75) return 'hsl(160,70%,45%)';
  if (v >= 50) return 'hsl(45,80%,50%)';
  return `hsl(${(v / 50) * 30},70%,55%)`;
}

function cat(p) {
  const x = Number(p ?? 0);
  if (x >= 90) return { label: 'Mükemmel',         cls: 're-cat-5' };
  if (x >= 75) return { label: 'Yeterli',           cls: 're-cat-4' };
  if (x >= 60) return { label: 'Ortalama Üstü',     cls: 're-cat-3' };
  if (x >= 45) return { label: 'Geliştirmeye Açık', cls: 're-cat-2' };
  return             { label: 'İyileştirilmeli',     cls: 're-cat-1' };
}

/* ── SVG chart ── */
const EXAM_TYPE_ORDER  = ['pretest', 'odev', 'quiz', 'vize', 'pro'];
const EXAM_TYPE_LABELS = { pretest: 'Ön Test', odev: 'Ödev', quiz: 'Quiz', vize: 'Vize', pro: 'Proje' };
const LINE_PALETTE = [
  '#0284c7','#7c3aed','#16a34a','#ea580c','#dc2626',
  '#0891b2','#9333ea','#15803d','#c2410c','#b45309',
];

function _sortOc(list) {
  return [...list].sort((a, b) => {
    const da = parseInt(a.code.replace(/\D/g, '')) || 0;
    const db = parseInt(b.code.replace(/\D/g, '')) || 0;
    return da - db;
  });
}

function OcTrendViz({ perExamData, perTypeData, weights }) {
  const [activeOc, setActiveOc] = useState(null);
  let exams = [];
  if (perExamData?.length >= 2) {
    exams = perExamData;
  } else {
    const availTypes = EXAM_TYPE_ORDER.filter(t => perTypeData?.[t]?.length > 0);
    if (availTypes.length < 2) return null;
    exams = availTypes.map(t => ({
      exam_id: t, title: EXAM_TYPE_LABELS[t] || t,
      exam_type: t, date: null, is_optical: false,
      outcomes: perTypeData[t],
    }));
  }
  if (exams.length < 2) return null;

  const typeTotals = {};
  exams.forEach(e => { typeTotals[e.exam_type] = (typeTotals[e.exam_type] || 0) + 1; });
  const typeIdx = {};
  const xLabels = exams.map(e => {
    typeIdx[e.exam_type] = (typeIdx[e.exam_type] || 0) + 1;
    const base = EXAM_TYPE_LABELS[e.exam_type] || e.exam_type;
    return typeTotals[e.exam_type] > 1 ? `${base} ${typeIdx[e.exam_type]}` : base;
  });

  const ocMap = {};
  exams.forEach((exam, idx) => {
    (exam.outcomes || []).forEach(item => {
      if (!ocMap[item.id]) ocMap[item.id] = { id: item.id, code: item.code, pts: Array(exams.length).fill(null) };
      ocMap[item.id].pts[idx] = item.avg_pct;
    });
  });
  const ocList = _sortOc(Object.values(ocMap));
  if (ocList.length === 0) return null;

  const W = 540, H = 186, ML = 38, MR = 16, MT = 14, MB = 44;
  const PW = W - ML - MR, PH = H - MT - MB;
  const xPos = i => ML + (exams.length > 1 ? i / (exams.length - 1) : 0.5) * PW;
  const yPos = v => MT + (1 - Math.max(0, Math.min(100, v)) / 100) * PH;
  const showValLabels = !activeOc && ocList.length <= 7;

  return (
    <div className="re-ocviz">
      <div className="re-ocviz-label">ÖÇ Trend Grafiği — Sınav Türüne Göre</div>
      <div className="re-ocviz-body">
        <svg viewBox={`0 0 ${W} ${H}`} className="re-oc-svg">
          {[0, 25, 50, 75, 100].map(v => (
            <g key={v}>
              <line x1={ML} y1={yPos(v)} x2={W - MR} y2={yPos(v)}
                stroke={v === 0 ? 'rgba(255,255,255,0.12)' : '#f1f5f9'} strokeWidth={v === 0 ? 1.5 : 1} />
              <text x={ML - 5} y={yPos(v) + 3.5} textAnchor="end" fontSize={8.5} fill="#94a3b8">{v}</text>
            </g>
          ))}
          {exams.map((exam, i) => (
            <g key={exam.exam_id}>
              <line x1={xPos(i)} y1={yPos(0)} x2={xPos(i)} y2={yPos(0) + 4} stroke="#cbd5e1" strokeWidth={1} />
              <text x={xPos(i)} y={H - MB + 12} textAnchor="middle" fontSize={9.5} fill="#334155" fontWeight="700">{xLabels[i]}</text>
              {exam.date && (
                <text x={xPos(i)} y={H - MB + 23} textAnchor="middle" fontSize={7.5} fill="#94a3b8">{exam.date}</text>
              )}
              {weights?.[exam.exam_type] && (
                <text x={xPos(i)} y={H - MB + 33} textAnchor="middle" fontSize={7.5} fill="#cbd5e1">
                  %{(weights[exam.exam_type] * 100).toFixed(0)} ağ.
                </text>
              )}
            </g>
          ))}
          {ocList.map((oc, oi) => {
            const color = LINE_PALETTE[oi % LINE_PALETTE.length];
            const isActive = activeOc === oc.id;
            const isDimmed = activeOc !== null && !isActive;
            const validPts = oc.pts.map((v, i) => v != null ? { x: xPos(i), y: yPos(v), v, i } : null).filter(Boolean);
            if (validPts.length === 0) return null;
            const d = validPts.map((p, pi) => `${pi === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
            return (
              <g key={oc.id} style={{ opacity: isDimmed ? 0.12 : 1 }}>
                <path d={d} fill="none" stroke={color} strokeWidth={isActive ? 3 : 2} strokeLinejoin="round" strokeLinecap="round" />
                {validPts.map((p, pi) => (
                  <g key={pi}>
                    <circle cx={p.x} cy={p.y} r={isActive ? 5 : 4} fill={color} stroke="#fff" strokeWidth={1.5}>
                      <title>{oc.code} — {xLabels[p.i]}: {p.v.toFixed(1)}%</title>
                    </circle>
                    {(showValLabels || isActive) && (
                      <text x={p.x} y={p.y - 9} textAnchor="middle" fontSize={isActive ? 9.5 : 8.5}
                        fill={color} fontWeight="800">{p.v.toFixed(0)}%</text>
                    )}
                  </g>
                ))}
              </g>
            );
          })}
        </svg>
        <div className="re-oc-legend">
          {ocList.map((oc, oi) => {
            const color = LINE_PALETTE[oi % LINE_PALETTE.length];
            const isActive = activeOc === oc.id;
            const isDimmed = activeOc !== null && !isActive;
            return (
              <div key={oc.id}
                className={`re-oc-legend-item ${isActive ? 're-oc-legend-active' : ''} ${isDimmed ? 're-oc-legend-dimmed' : ''}`}
                onClick={() => setActiveOc(isActive ? null : oc.id)}>
                <svg width="22" height="12" className="re-oc-legend-line">
                  <line x1="1" y1="6" x2="21" y2="6" stroke={color} strokeWidth={isActive ? 3 : 2} strokeLinecap="round" />
                  <circle cx="11" cy="6" r={isActive ? 4 : 3} fill={color} />
                </svg>
                <span>{oc.code}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function OutcomeRow({ item }) {
  const c = cat(item.avg_pct);
  return (
    <div className={`re-oc-row re-oc-colored ${c.cls}`}>
      <span className="re-oc-code">{item.code}</span>
      <span className="re-oc-desc" title={item.description}>{item.description}</span>
      <div className="re-oc-bar-track">
        <div className="re-oc-bar-fill" style={{ width: `${item.avg_pct ?? 0}%`, background: pctColor(item.avg_pct) }} />
      </div>
      <span className={`re-badge ${c.cls}`}>{item.avg_pct != null ? item.avg_pct.toFixed(1) : '—'}</span>
      <span className={`re-label ${c.cls}`}>{c.label}</span>
      <span className="re-oc-count">{item.student_count} öğr</span>
    </div>
  );
}

function CourseAnalysisPanel({ course, analysis, loading }) {
  if (loading) return (
    <div className="re-analysis-panel">
      <div className="re-analysis-loading">
        <span className="re-spinner" /> {course.course_name} analizi yükleniyor…
      </div>
    </div>
  );
  if (!analysis) return null;
  if (analysis.error) return (
    <div className="re-analysis-panel">
      <div className="re-analysis-error">{analysis.error}</div>
    </div>
  );

  const loList      = analysis.class_averages?.learning_outcomes || [];
  const poList      = analysis.class_averages?.program_outcomes  || [];
  const overall     = analysis.class_averages?.overall_post_midterm_pct;
  const perExamData = analysis.class_averages?.per_exam_learning_outcomes || [];
  const perTypeLO   = analysis.class_averages?.per_type_learning_outcomes || {};
  const students    = analysis.students || [];
  const examTypes   = Object.keys(analysis.exam_type_weights || {});
  const weights     = analysis.exam_type_weights || {};
  const instructor  = analysis.assigned_instructor;

  return (
    <div className="re-analysis-panel">
      <div className="re-analysis-header">
        <div className="re-analysis-title">{course.course_name}</div>
        {instructor && <div className="re-analysis-user">{instructor}</div>}
        <div className="re-analysis-meta">
          <span className="re-meta-pill">{students.length} öğrenci</span>
          {examTypes.map(t => <span key={t} className="re-meta-chip">{t}</span>)}
          {overall != null && (
            <span className="re-meta-pill re-meta-overall">
              Sınıf Genel: <strong>{overall.toFixed(1)}</strong>
            </span>
          )}
        </div>
      </div>

      <OcTrendViz perExamData={perExamData} perTypeData={perTypeLO} weights={weights} />

      {loList.length > 0 && (
        <div className="re-analysis-section">
          <div className="re-section-label">Öğrenme Çıktıları Sınıf Ortalaması</div>
          <div className="re-oc-list">
            {loList.map(o => <OutcomeRow key={o.id} item={o} />)}
          </div>
        </div>
      )}

      {poList.length > 0 && (
        <div className="re-analysis-section">
          <div className="re-section-label re-section-label-po">Program Öğrenme Çıktıları Sınıf Ortalaması</div>
          <div className="re-oc-list">
            {poList.map(o => <OutcomeRow key={o.id} item={o} />)}
          </div>
        </div>
      )}

      {loList.length === 0 && poList.length === 0 && (
        <div className="re-no-data">
          Bu ders için henüz ÖÇ/PÖÇ verisi bulunmuyor. Sınav sonuçları yüklenmemiş olabilir.
        </div>
      )}
    </div>
  );
}

/* ── Main ── */
export default function ProgramBaskaniExamResults() {
  const [roleData, setRoleData]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseAnalysis, setCourseAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [search, setSearch]             = useState('');
  const [treeOpen, setTreeOpen]         = useState(true);

  const akademisyenId = sessionStorage.getItem('akademisyen_id');

  useEffect(() => {
    if (!akademisyenId) {
      setError('Akademisyen bilgisi bulunamadı'); setLoading(false); return;
    }
    axios.get(`${API_ENDPOINTS.PROGRAM_BASKANI_EXAM_RESULTS}?akademisyen_id=${akademisyenId}`)
      .then(r => setRoleData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Veri yüklenemedi'))
      .finally(() => setLoading(false));
  }, [akademisyenId]);

  const handleCourseSelect = useCallback(async (course) => {
    if (selectedCourse?.course_id === course.course_id) {
      setSelectedCourse(null); setCourseAnalysis(null); return;
    }
    setSelectedCourse(course);
    setTreeOpen(false);
    setCourseAnalysis(null);
    setAnalysisLoading(true);
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/pre-midterm/${roleData?.archived_year ? 'term-final-class-breakdown' : 'post-midterm-class-breakdown'}?course_id=${course.course_id}`,
        { withCredentials: true }
      );
      setCourseAnalysis(r.data);
    } catch (e) {
      setCourseAnalysis({ error: e.response?.data?.error || 'Analiz yüklenemedi' });
    } finally {
      setAnalysisLoading(false);
    }
  }, [selectedCourse, roleData?.archived_year]);

  const q = search.trim().toLowerCase();
  const courses = (roleData?.courses || []).filter(c =>
    !q || c.course_name.toLowerCase().includes(q)
  );
  const totalStudents = (roleData?.courses || []).reduce((s, c) => s + (c.total_students || 0), 0);

  if (loading) return (
    <div className="re-wrap">
      <div className="re-loading"><span className="re-spinner" /> Program verileri yükleniyor…</div>
    </div>
  );

  if (error && !roleData) return (
    <div className="re-wrap">
      <div className="re-error-box">{error}</div>
    </div>
  );

  return (
    <div className="re-wrap">

      {/* Hero */}
      <div className="re-hero">
        <div className="re-hero-left">
          <div className="re-role-badge">Program Başkanı</div>
          <h2 className="re-hero-title">Program Sınav Sonuçları</h2>
          <p className="re-hero-sub">
            {roleData?.program_name && <><strong>{roleData.program_name}</strong> · </>}
            {roleData?.faculty_name}
          </p>
          {roleData?.academician_name && (
            <div className="re-hero-user">{roleData.academician_name}</div>
          )}
        </div>
        <div className="re-hero-stats">
          <AnimStat value={courses.length} label="Ders" />
          <AnimStat value={totalStudents} label="Girdi" />
        </div>
      </div>

      {error && <div className="re-error-box">{error}</div>}

      {courses.length === 0 && !loading && !q && (
        <div className="re-empty">Henüz sınav verisi olan ders bulunmuyor.</div>
      )}

      <div className={`re-body${treeOpen ? '' : ' re-body-collapsed'}`}>
        {/* Sol: ders listesi */}
        <div className="re-tree-wrap">
          {!treeOpen ? (
            <button className="re-tree-toggle" onClick={() => setTreeOpen(true)} title="Listeyi aç">☰</button>
          ) : (
            <div className="re-tree">
              <div className="re-search-wrap">
                <span className="re-search-icon">🔍</span>
                <input
                  className="re-search-input"
                  type="text"
                  placeholder="Ders ara…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button className="re-search-clear" onClick={() => setSearch('')}>✕</button>
                )}
              </div>

              {courses.length === 0 && q && (
                <div className="re-search-empty">"{search}" için sonuç bulunamadı</div>
              )}

              {/* Tek bir "fakülte bloğu" gibi wrapper — program adı başlık */}
              <div className="re-faculty-block">
                <div className="re-faculty-header re-open" style={{ cursor: 'default' }}>
                  <div className="re-faculty-info">
                    <span className="re-faculty-icon">▾</span>
                    <span className="re-faculty-name">{roleData?.program_name || 'Program'}</span>
                  </div>
                  <div className="re-faculty-badges">
                    <span className="re-tree-badge">{courses.length} ders</span>
                  </div>
                </div>
                <div className="re-programs">
                  <div className="re-course-list" style={{ paddingLeft: 10 }}>
                    {courses.length === 0 && (
                      <div className="re-course-empty">Sınav verisi yok</div>
                    )}
                    {courses.map(course => {
                      const isSelected = selectedCourse?.course_id === course.course_id;
                      return (
                        <button
                          key={course.course_id}
                          className={`re-course-card ${isSelected ? 're-course-selected' : ''}`}
                          onClick={() => handleCourseSelect(course)}
                        >
                          <div className="re-course-name">{course.course_name}</div>
                          <div className="re-course-meta">
                            <span className="re-course-students">{course.total_students} girdi</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sağ: analiz */}
        <div className="re-detail">
          {!selectedCourse && (
            <div className="re-detail-empty">
              <div className="re-detail-empty-icon">📊</div>
              <div className="re-detail-empty-text">Soldan bir ders seçin</div>
              <div className="re-detail-empty-sub">
                ÖÇ/PÖÇ sınıf ortalamalarını görmek için herhangi bir derse tıklayın
              </div>
            </div>
          )}
          {selectedCourse && (
            <CourseAnalysisPanel
              course={selectedCourse}
              analysis={courseAnalysis}
              loading={analysisLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
}
